import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
  useToast,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { SupportService } from "@modules";
import { Mail } from "lucide-react";
import { useCallback } from "react";

type SupportServiceDetailProps = {
  service: SupportService;
};

export function SupportServiceDetail({ service }: SupportServiceDetailProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const handleCopyEmail = useCallback(() => {
    navigator.clipboard.writeText(service.email);
    toast.add({
      title: t("hub.support.copied_title", { defaultValue: "Copied!" }),
      description: t("hub.support.copied_email_description", {
        defaultValue: "Email address copied to clipboard.",
      }),
    });
  }, [service.email, t, toast]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t(service.detailTitleKey, {
            defaultValue: service.detailTitleDefault,
          })}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ItemTitle className="font-semibold">
          {t("hub.support.email_us", { defaultValue: "Email Us" })}
        </ItemTitle>
        <Item variant="outline" size="xs" className="flex-nowrap">
          <ItemMedia className="group-hover/item:bg-transparent">
            <div className="flex size-8 items-center justify-center rounded-full bg-muted">
              <Mail className="size-4 text-muted-foreground" aria-hidden />
            </div>
          </ItemMedia>
          <ItemContent className="min-w-0">
            <ItemTitle className="truncate">{service.email}</ItemTitle>
          </ItemContent>
          <ItemActions>
            <Button
              size="sm"
              onClick={handleCopyEmail}
              aria-label={t("hub.support.copy_email_aria", {
                defaultValue: "Copy email address",
              })}>
              {t("hub.support.copy_email", {
                defaultValue: "Copy email",
              })}
            </Button>
          </ItemActions>
        </Item>
      </CardContent>
    </Card>
  );
}
